import { ICellEditor, ICellEditorParams } from "ag-grid-community";
import {
  type ExtensionFieldObjectType,
  type ExtensionFieldEnumObjectType,
} from "~/graphql/supergraphTypes";
import { EXTENSION_FIELD_TYPE_IDS } from "../../../utils/extensionFieldUtils";

/**
 * Mock AG Grid API interface for testing
 */
export interface MockGridApi {
  stopEditing: jest.Mock;
}

/**
 * Mock AG Grid Row Node interface for testing
 */
export interface MockRowNode {
  setDataValue?: jest.Mock;
  id?: string;
}

/**
 * Mock AG Grid Column interface for testing
 */
export interface MockColumn {
  field?: string;
}

/**
 * Mock AG Grid Column Definition interface for testing
 */
export interface MockColDef {
  field?: string;
}

/**
 * Mock AG Grid Column API interface for testing
 */
// eslint-disable-next-line @typescript-eslint/no-empty-object-type
export interface MockColumnApi {
  // Add specific methods as needed
}

/**
 * Test cell editor parameters type that allows for flexible mocking
 */
export interface TestCellEditorParams {
  value: unknown;
  fieldType: number;
  fieldId: string;
  enumValues?: ExtensionFieldEnumObjectType[];
  field: ExtensionFieldObjectType;
  api: MockGridApi;
  node: MockRowNode;
  data: Record<string, unknown>;
  colDef: MockColDef;
  column: MockColumn;
  rowIndex: number;
  context: Record<string, unknown>;
  onKeyDown: jest.Mock;
  stopEditing: jest.Mock;
  eGridCell: HTMLElement;
  parseValue: jest.Mock;
  formatValue: jest.Mock;
  eventKey: null;
  cellStartedEdit: boolean;
  columnApi: MockColumnApi;
}

/**
 * Common interface for all cell editor parameters
 */
export interface CellEditorParams extends Omit<
  ICellEditorParams,
  "api" | "node" | "data" | "colDef" | "column" | "columnApi"
> {
  fieldType: number;
  fieldId: string;
  enumValues?: ExtensionFieldEnumObjectType[];
  field: ExtensionFieldObjectType;
  api: MockGridApi | ICellEditorParams["api"];
  node: MockRowNode | ICellEditorParams["node"];
  // eslint-disable-next-line @typescript-eslint/no-redundant-type-constituents
  data: Record<string, unknown> | ICellEditorParams["data"];
  colDef: MockColDef | ICellEditorParams["colDef"];
  column: MockColumn | ICellEditorParams["column"];
  columnApi: MockColumnApi | ICellEditorParams["columnApi"];
}

/**
 * Base interface that all cell editors must implement
 */
export interface ExtensionFieldCellEditor extends ICellEditor {
  init(params: CellEditorParams): void;
  getGui(): HTMLElement;
  getValue(): string;
  destroy?(): void;
  isPopup?(): boolean;
  isCancelBeforeStart?(): boolean;
  isCancelAfterEnd?(): boolean;
  focusIn?(): void;
  focusOut?(): void;
  afterGuiAttached?(): void;
}

/**
 * Field type to cell editor component name mapping
 * Used by AG Grid to determine which editor to use for each field type
 */
export const FIELD_TYPE_CELL_EDITORS = {
  [EXTENSION_FIELD_TYPE_IDS.TEXT]: "TextCellEditor",
  [EXTENSION_FIELD_TYPE_IDS.INTEGER]: "NumberCellEditor",
  [EXTENSION_FIELD_TYPE_IDS.FLOAT]: "NumberCellEditor",
  [EXTENSION_FIELD_TYPE_IDS.ENUMERATION]: "EnumerationCellEditor",
  [EXTENSION_FIELD_TYPE_IDS.DATE]: "DateCellEditor",
  [EXTENSION_FIELD_TYPE_IDS.CURRENCY_USD]: "CurrencyCellEditor",
  [EXTENSION_FIELD_TYPE_IDS.PERCENTILE]: "PercentileCellEditor",
} as const;

/**
 * Field type to column filter component name mapping
 * Used by AG Grid to determine which filter to use for each field type
 */
export const FIELD_TYPE_COLUMN_FILTERS = {
  [EXTENSION_FIELD_TYPE_IDS.TEXT]: "agTextColumnFilter",
  [EXTENSION_FIELD_TYPE_IDS.INTEGER]: "agNumberColumnFilter",
  [EXTENSION_FIELD_TYPE_IDS.FLOAT]: "agNumberColumnFilter",
  [EXTENSION_FIELD_TYPE_IDS.ENUMERATION]: "agSetColumnFilter",
  [EXTENSION_FIELD_TYPE_IDS.DATE]: "agDateColumnFilter",
  [EXTENSION_FIELD_TYPE_IDS.CURRENCY_USD]: "agNumberColumnFilter",
  [EXTENSION_FIELD_TYPE_IDS.PERCENTILE]: "agNumberColumnFilter",
} as const;

/**
 * Get cell editor name for a given field type
 */
export const getCellEditorForFieldType = (fieldType: number): string => {
  const editorName =
    FIELD_TYPE_CELL_EDITORS[fieldType as keyof typeof FIELD_TYPE_CELL_EDITORS] ||
    "TextCellEditor";

  return editorName;
};

/**
 * Get column filter name for a given field type
 */
export const getColumnFilterForFieldType = (fieldType: number): string => {
  const filterName =
    FIELD_TYPE_COLUMN_FILTERS[fieldType as keyof typeof FIELD_TYPE_COLUMN_FILTERS] ||
    "agTextColumnFilter";

  return filterName;
};
